import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { navigate, navigateRaw } from '@/services/navigation';

import moment from 'moment';
import { App } from '@/services/app-compat';
import { api } from '@/services/api-class';
import { getModuleType } from '@/utils/legacy-utils';
const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const displayNocValuesForGridTemplate = _wg('displayNocValuesForGridTemplate');

interface NPSDCallCardsGridProps {
  entityId: number | string | null;
  entityType: string; // 'person' or 'vehicle'
  entityIdField?: string; // API field name: 'personId' or 'vehicleId'
  title?: string;
  active?: boolean;
  showRefresh?: boolean;
}

export function NPSDCallCardsGrid({
  entityId = null,
  entityType,
  entityIdField = 'id',
  title = 'Call Cards',
  active = true,
  showRefresh = false,
}: NPSDCallCardsGridProps) {
  const { theme } = useTheme();

  const [isMobile, setIsMobile] = useState(false);
  const [callCards, setCallCards] = useState<any[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [hasLoaded, setHasLoaded] = useState(false);

  const prevEntityIdRef = useRef(entityId);
  const prevActiveRef = useRef(active);

  const showPagination = useMemo((): boolean => {
    return totalPages > 1;
  }, [totalPages]);

  const recordCount = useMemo((): number => {
    return totalRecords || 0;
  }, [totalRecords]);

  // Check if mobile (debounced to avoid excessive re-renders during resize)
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const checkIfMobile = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => setIsMobile(window.innerWidth <= 1024), 150);
    };
    setIsMobile(window.innerWidth <= 1024);
    window.addEventListener('resize', checkIfMobile);
    return () => {
      window.removeEventListener('resize', checkIfMobile);
      if (timer) clearTimeout(timer);
    };
  }, []);

  const loadCallCards = useCallback((page: number) => {
    if (!entityId) {
      setCallCards([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    const operation = {
      data: {
        page: page,
        take: pageSize,
        skip: (page - 1) * pageSize,
      },
    };

    api.callCardSearchByNpsd(
      entityId,
      entityType,
      entityIdField,
      page,
      String(pageSize),
      JSON.stringify(operation),
      (result: any) => {
        setCallCards(result.entries || []);
        setTotalRecords(result.records || 0);
        setTotalPages(Math.ceil((result.records || 0) / pageSize) || 1);
        setIsLoading(false);
        setHasLoaded(true);
      }
    );
  }, [entityId, entityType, entityIdField, pageSize]);

  // Initial load on mount
  useEffect(() => {
    if (active && entityId) {
      loadCallCards(1);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Watch active changes (lazy loading)
  useEffect(() => {
    if (active !== prevActiveRef.current) {
      prevActiveRef.current = active;
      if (active && entityId && !hasLoaded) {
        loadCallCards(1);
      }
    }
  }, [active, entityId, hasLoaded, loadCallCards]);

  // Watch entityId changes
  useEffect(() => {
    if (entityId !== prevEntityIdRef.current) {
      prevEntityIdRef.current = entityId;
      if (entityId && active) {
        setCurrentPage(1);
        setHasLoaded(false);
        loadCallCards(1);
      }
    }
  }, [entityId, active, loadCallCards]);

  const goToPage = useCallback((p: number) => {
    if (p < 1 || p > totalPages) return;
    setCurrentPage(p);
    loadCallCards(p);
  }, [totalPages, loadCallCards]);

  const refresh = useCallback(() => {
    setCurrentPage(1);
    setHasLoaded(false);
    loadCallCards(1);
  }, [loadCallCards]);

  const formatDateTime = useCallback((dateStr: string): string => {
    if (!dateStr) return '';
    if (typeof moment !== 'undefined') {
      return moment(dateStr).format('MM/DD/YYYY h:mm A');
    }
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleString();
  }, []);

  const formatNatureOfCall = useCallback((entry: any): string => {
    if (typeof (window as any).displayNocValuesForGridTemplate === 'function') {
      return displayNocValuesForGridTemplate(entry);
    }
    if (entry.natureOfCallValues && Array.isArray(entry.natureOfCallValues)) {
      return entry.natureOfCallValues.join(', ');
    }
    return '';
  }, []);

  const getCallCardLink = useCallback((callCard: any): string => {
    const moduleType = typeof getModuleType === 'function'
      ? getModuleType().toUpperCase()
      : (typeof App !== 'undefined' ? App.getModuleTypeFromURL().toUpperCase() : 'CAD');
    const functionName = moduleType === 'CAD' ? 'CAD_ShowCallCard' : 'MDT_ShowCallCard';
    return `/?sectionName=${moduleType}&tab=Search&function=${functionName}&multiModule=true&params=${callCard.id}`;
  }, []);

  const viewCallCard = useCallback((callCard: any) => {
    navigateRaw(getCallCardLink(callCard));
  }, [getCallCardLink]);

  return (
    <div className={`npsd-callcards-grid ${theme}`}>
      <div className="panel">
        <div className="panel-header">
          <span>{title}</span>
          {hasLoaded && <span className="record-count">({recordCount} records)</span>}
          {showRefresh && (
            <button type="button" className="btn-refresh" onClick={refresh} title="Refresh">
              <i className="fa fa-sync-alt"></i>
            </button>
          )}
        </div>
        <div className="panel-body">
          {/* Loading State */}
          {isLoading && (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <div>Loading call cards...</div>
            </div>
          )}

          {/* Desktop Table */}
          {!isLoading && !isMobile && (
            <div className="table-container">
              {(callCards ?? []).length > 0 ? (
                <>
                  <table>
                    <thead>
                      <tr>
                        <th style={{ width: '75px' }}>Event ID</th>
                        <th style={{ width: '85px' }}>Date/Time</th>
                        <th style={{ width: '175px' }}>Nature of Call</th>
                        <th style={{ width: '175px' }}>Location</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(callCards ?? []).map((callCard, index) => (
                        <tr key={'callcard-' + index}>
                          <td>
                            <a href={getCallCardLink(callCard)} className="link-primary">
                              {callCard.eventId}
                            </a>
                          </td>
                          <td>{formatDateTime(callCard.callDateTime)}</td>
                          <td dangerouslySetInnerHTML={{ __html: formatNatureOfCall(callCard) }}></td>
                          <td>{callCard.callLocFormattedAddress}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Pagination */}
                  {showPagination && (
                    <div className="pagination">
                      <span className="pagination-info">
                        Page {currentPage} of {totalPages}
                      </span>
                      <div className="pagination-controls">
                        <button
                          type="button"
                          className="btn-page"
                          onClick={() => goToPage(currentPage - 1)}
                          disabled={currentPage <= 1}
                        >
                          &laquo; Previous
                        </button>
                        <button
                          type="button"
                          className="btn-page"
                          onClick={() => goToPage(currentPage + 1)}
                          disabled={currentPage >= totalPages}
                        >
                          Next &raquo;
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                hasLoaded && (
                  <div className="empty-state">No call cards involving this {entityType}</div>
                )
              )}
            </div>
          )}

          {/* Mobile Cards */}
          {!isLoading && isMobile && (
            <div className="mobile-cards">
              {(callCards ?? []).map((callCard, index) => (
                <div key={'callcard-mobile-' + index} className="card">
                  <div className="card-header">
                    <a href={getCallCardLink(callCard)} className="card-title">
                      {callCard.eventId}
                    </a>
                    <span className="datetime">{formatDateTime(callCard.callDateTime)}</span>
                  </div>
                  <div className="card-body">
                    <div className="card-field">
                      <span className="field-label">Nature of Call:</span>
                      <span className="field-value" dangerouslySetInnerHTML={{ __html: formatNatureOfCall(callCard) }}></span>
                    </div>
                    {callCard.callLocFormattedAddress && (
                      <div className="card-field">
                        <span className="field-label">Location:</span>
                        <span className="field-value">{callCard.callLocFormattedAddress}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {hasLoaded && (callCards ?? []).length === 0 && (
                <div className="empty-state">No call cards involving this {entityType}</div>
              )}

              {/* Mobile Pagination */}
              {showPagination && (
                <div className="pagination mobile-pagination">
                  <button
                    type="button"
                    className="btn-page"
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage <= 1}
                  >
                    &laquo;
                  </button>
                  <span className="pagination-info">{currentPage} / {totalPages}</span>
                  <button
                    type="button"
                    className="btn-page"
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                  >
                    &raquo;
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

(window as any).NPSDCallCardsGridReact = NPSDCallCardsGrid;
